import React from 'react';
import { connect } from 'react-redux';
import { OverlayDrawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter, Button, Spinner, Label, Text, Popover, PopoverTrigger, PopoverSurface, Field, Input, Link, Subtitle1 } from '@fluentui/react-components';
import { Edit12Regular, CalendarToday28Regular, Dismiss24Regular } from '@fluentui/react-icons';
import moment from 'moment';

// functions
import * as ComponentFunctions from './PractitionerConsumptionDetailsMini.Functions';
import { VisitTypeEnum } from '../../../../models/Enums/VisitTypeEnum';

// reutilizamos estilos del panel grande para mantener consistencia visual
import '../../../Protocols/ConsumptionDetails/ConsumptionDetails.Styles.css';

const getVisitTypeLabel = (value: number | undefined) => {
  switch (value) {
    case VisitTypeEnum.IN_PERSON: return 'Presencial';
    case VisitTypeEnum.TELEHEALTH: return 'Remota';
    case VisitTypeEnum.HOME_VISIT: return 'Domicilio';
    case VisitTypeEnum.OTHER: return 'Otro';
    default: return '-';
  }
};

const PractitionerConsumptionDetailsMiniComponent = (props: ComponentFunctions.Props) => {

  const statusDescription = ComponentFunctions.GetStatusDescription(props.Consumption?.status ?? -1);

  // estado local para edición de monto
  const [isAmountPopoverOpen, setIsAmountPopoverOpen] = React.useState(false);
  const [newUnitAmount, setNewUnitAmount] = React.useState<number | undefined>(props.Consumption?.unitAmount);
  const [newCurrency, setNewCurrency] = React.useState<string>(props.Consumption?.currencyCode || 'USD');
  React.useEffect(() => {
    // cerrar al terminar guardado exitoso (cuando deja de estar isSaving y backend refleja nuevo valor)
    if(!props.isSaving && isAmountPopoverOpen && props.Consumption?.unitAmount === newUnitAmount){
      setIsAmountPopoverOpen(false);
    }
  }, [props.isSaving, props.Consumption?.unitAmount]);

  const onSaveAmount = () => {
    if (!props.Consumption || newUnitAmount === undefined || Number.isNaN(newUnitAmount)) return;
    props.ChangeAmount(
      props.Consumption.practitionerId,
      props.Consumption.id,
      newUnitAmount,
      newCurrency
    );
  };

  return (
    <OverlayDrawer open={props.isOpen} onOpenChange={(_, data) => { if (!data.open) props.onClose(); }} position='end' size='medium'>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance='subtle'
              aria-label='Close'
              icon={<Dismiss24Regular />}
              onClick={() => props.onClose()}
            />
          }
        >
          <div className='consumptionHeader'>
            <CalendarToday28Regular style={{ height: '2.5em', width: '2.5em' }} />
            <div>
              <Subtitle1 style={{ textTransform: 'capitalize' }}>{props.Consumption?.serviceName || 'Detalle de consumo'}</Subtitle1>
              <Text>{props.Consumption?.stageName}</Text>
            </div>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {props.isLoading && <Spinner />}
        {props.Consumption && !props.isLoading && (
          <div>
            <div className='consumptionDetails'>
              <p>
                <Label>Paciente</Label>
                <Text>{props.Consumption.patientNumberInProtocol}</Text>
              </p>
              <p>
                <Label>Fecha de realización</Label>
                <Text>{props.Consumption.serviceDueDate ? moment(props.Consumption.serviceDueDate).format('DD/MM/YYYY HH:mm') : '-'}</Text>
              </p>
              <p>
                <Label>Estado</Label>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={'status-mark status-' + statusDescription.class}></span>
                  <Text>{statusDescription.text}</Text>
                </span>
                {props.Consumption?.settlementId && props.Consumption.status === 3 && (
                  <div style={{ marginTop: 4 }}>
                    <Label style={{ display: 'block' }}>Liquidación</Label>
                    <Link href={'../settlements/' + props.Consumption.settlementId} target='_blank'>
                      {props.Consumption.settlementIdentifier || 'Ver liquidación'}
                    </Link>
                  </div>
                )}
              </p>
              <p>
                <Label>Visita</Label>
                <Text>{props.Consumption.stageName || 'Sin visita asociada'}</Text>
              </p>
              <p>
                <Label>Tipo de visita</Label>
                <Text>{getVisitTypeLabel(props.Consumption.realizedType)}</Text>
              </p>
            </div>
            <hr />
            <div className='consumptionGrandTotal'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  MONTO UNITARIO
                  <Popover withArrow open={isAmountPopoverOpen} onOpenChange={(_, data) => setIsAmountPopoverOpen(!!data.open)}>
                    <PopoverTrigger disableButtonEnhancement>
                      <Button appearance='subtle' size='small' icon={<Edit12Regular />} />
                    </PopoverTrigger>
                    <PopoverSurface tabIndex={-1} aria-label='Editar monto unitario'>
                      <div style={{ minWidth: 220 }}>
                        <Field label='Monto'>
                          <Input
                            type='number'
                            step={0.01}
                            value={newUnitAmount?.toString() ?? ''}
                            onChange={(e) => setNewUnitAmount(parseFloat(e.target.value))}
                          />
                        </Field>
                        <Field label='Moneda'>
                          <Input
                            value={newCurrency}
                            onChange={(e) => setNewCurrency(e.target.value.toUpperCase())}
                          />
                          {/* TODO: Reemplazar por CurrencySelectorComponent si se desea consistencia */}
                        </Field>
                        <hr />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <Button appearance='secondary' onClick={() => { setIsAmountPopoverOpen(false); setNewUnitAmount(props.Consumption?.unitAmount); setNewCurrency(props.Consumption?.currencyCode || 'USD'); }}>Cancelar</Button>
                          <Button appearance='primary' disabled={props.isSaving || newUnitAmount === undefined || Number.isNaN(newUnitAmount)} onClick={onSaveAmount}>
                            {props.isSaving && <Spinner size='extra-tiny' />} Guardar
                          </Button>
                        </div>
                      </div>
                    </PopoverSurface>
                  </Popover>
                </Label>
                <Text>{props.Consumption.unitAmount?.toLocaleString('es-AR', { style: 'currency', currency: props.Consumption.currencyCode?.toUpperCase() }) ?? '0.00'}</Text>
              </div>
              <div>
                <Label>TOTAL</Label>
                <Text>{props.Consumption.amount?.toLocaleString('es-AR', { style: 'currency', currency: props.Consumption.currencyCode?.toUpperCase() }) ?? '0.00'}</Text>
              </div>
              <div>
                <Label>PENDIENTE</Label>
                <Text>{props.Consumption.pendingAmount?.toLocaleString('es-AR', { style: 'currency', currency: props.Consumption.currencyCode?.toUpperCase() }) ?? '0.00'}</Text>
              </div>
            </div>
            <hr />
            <div className='RelatedConceptContainer'>
              <Label>Concepto</Label>
              <Text>{props.Consumption.conceptDescription || '-'}</Text>
            </div>
            {(props.Consumption.comments || props.Consumption.moreInfoComments) && <>
              <hr />
              <div className='RelatedConceptContainer'>
                <Label>Comentarios</Label>
                <Text>{props.Consumption.comments || props.Consumption.moreInfoComments}</Text>
              </div>
            </>}
          </div>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button appearance='primary' onClick={() => props.onClose()}>Cerrar</Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionDetailsMiniComponent as any);
